import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Preloader, Tabs } from '@smartshell/ui-kit';

import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';

import { ESlice, EType, TNormalizedItem } from '../types';
import { formatPeople } from '../utils';
import { Chart } from './Chart';
import s from './ChartBlock.module.scss';

interface IChartBlockProps {
  data: Array<TNormalizedItem>;
  total: number;
  totalClients: number;
  totalGuests: number;
  totalNewClients: number;
  totalClientSeances: number;
  loading: boolean;
  called: boolean;
  slice: ESlice;
  setSlice: Dispatch<SetStateAction<ESlice>>;
}
export const ChartBlock: FC<IChartBlockProps> = ({
  data,
  total,
  totalClients,
  totalGuests,
  loading,
  called,
  slice,
  setSlice,
  totalClientSeances,
  totalNewClients,
}) => {
  const { t } = useContext(LocalizationContext);

  const [TYPE_OPTIONS, SLICE_OPTIONS] = useMemo(
    () => [
      [
        { value: EType.ALL, title: t('all.visitors') },
        { value: EType.UNIQUE, title: t('visitors.types.unique') },
        { value: EType.NEW, title: t('visitors.types.new') },
      ],
      [
        { value: ESlice.DAY, title: t('visitors.slice.day') },
        { value: ESlice.WEEK, title: t('visitors.slice.week') },
        { value: ESlice.MONTH, title: t('visitors.slice.month') },
      ],
    ],
    [t]
  );

  const [type, setType] = useState<EType>(EType.ALL);
  const { max, min } = useMemo(
    () =>
      data.reduce(
        (acc, item) => {
          if (item.total > acc.max) {
            acc.max = item.total;
          }
          if (item.total < acc.min) {
            acc.min = item.total;
          }

          return acc;
        },
        {
          max: 0,
          min: data[0]?.total ?? 0,
        }
      ),
    [data]
  );

  const size = useWindowResize();

  return (
    <div className={s.container}>
      <div className={s.top}>
        <div className={s.header}>
          <div className={s.controls}>
            {size.width > 960 && (
              <Tabs
                options={TYPE_OPTIONS}
                onChange={setType}
                value={type}
                disabled={loading}
                className={s.type}
              />
            )}
            <Tabs
              options={SLICE_OPTIONS}
              onChange={setSlice}
              value={slice}
              disabled={loading}
              className={s.slice}
            />
          </div>
        </div>
        <div className={s.info}>
          <div className={s.people}>
            <span className={s.title}>{t('visitors.total')}</span>
            {!called || loading || data.length === 0 ? (
              '—'
            ) : (
              <span>
                {total} {t('contracted.persons')}
              </span>
            )}
            {!loading && totalClients > 0 && (
              <div className={s.count}>
                <span className={s.green}>{t('visitors.max', max)}</span>
                <span className={s.red}>{t('visitors.min', min || '0')}</span>
              </div>
            )}
          </div>

          <div className={s.separator} />

          <div className={s.people_block}>
            <div className={s.people}>
              <span className={s.title}>{t('clients')}</span>
              {!called || loading || data.length === 0 ? (
                '—'
              ) : (
                <>
                  <span>{formatPeople(totalClients, t)}</span>
                  {totalNewClients > 0 && (
                    <span className={s.green}>
                      +{totalNewClients} {t('clients.new')}
                    </span>
                  )}
                </>
              )}
            </div>

            <div className={s.people}>
              <span className={s.title}>{t('visitors.summary.seances')}</span>
              {!called || loading || data.length === 0 ? (
                '—'
              ) : (
                <span>
                  {totalClientSeances} {t('pcs')}
                </span>
              )}
            </div>
          </div>

          <div className={s.separator} />

          <div className={s.people}>
            <span className={s.title}>{t('guests')}</span>
            {!called || loading || data.length === 0 ? (
              '—'
            ) : (
              <span className={s.data}>{formatPeople(totalGuests, t)}</span>
            )}
          </div>
        </div>
      </div>
      {size.width > 960 && (
        <Preloader
          loading={loading}
          hasData={called && data.length > 0}
          noDataPlaceholder={t(
            called ? 'report.placeholder' : 'report.placeholder.emptyDates'
          )}
        >
          <Chart type={type} data={data} />
        </Preloader>
      )}
    </div>
  );
};
